import React, {useCallback, useMemo, useState, useRef} from 'react'
import {useTranslation} from 'i18n'
import {ForkygramTemplatePostContent} from 'pages'
import {requestData} from 'services'
import {
  ForkygramPostActionOptions,
  ForkygramPostActionOptionsFeed,
  ForkygramReportCategoriesResponse,
} from 'types'
import {
  getCollaboratorMember,
  showModalLimitedAccess,
  showModalReprocessKyc,
  showSnackbar,
  useDidMount,
  useHistory,
} from 'utils'
import {ConfirmationModal, InputPicker} from 'common/components'
import {useSelector} from 'lib/redux'
import {TemplateProfileSelfOptionModal} from '../../../../template'
import {
  GiftShopTemplateOptionsContent,
  GiftShopTemplateOptionsItem,
  GiftShopTemplateOptionsModal,
  GiftShopTemplateReportModal,
  GiftShopTemplateShareModal,
} from '../../../template'
import {ValidateKYCModal} from '../ValidateKYCModal'
import {GiftShopFeedContentProps} from './GiftShopFeedContentProps'

export default function GiftShopFeedContent({
  data,
  index,
  visibilityIndex,
  stateMute,
  stateVideoPause,
  setVideoPlaying,
  onBlocked,
  onReported,
  onEdited,
  onDeleted,
  onFollowed,
  onCollabRemoved,
}: GiftShopFeedContentProps) {
  const {translate} = useTranslation()
  const pause = stateVideoPause[0]
  const [modal, setModal] = useState<ForkygramPostActionOptionsFeed>('none')
  const [selfModal, setSelfModal] = useState<ForkygramPostActionOptions>('none')
  const {access_token, id} = useSelector('user') || {}
  const {status: childStatus} = useSelector('parentalConsentState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const {kycStatus} = useSelector('yuserActivationState')
  const history = useHistory()
  const ref = useRef<HTMLInputElement>(null)
  const stateRot = useState(0)
  const [rot, setRot] = stateRot
  const currentRot = useRef(0)

  const {collaboratorOwner, collaboratorSelf} = getCollaboratorMember(
    data.collaborator_members,
  )

  const handleToggleModal = useCallback(() => {
    setSelfModal('none')
    setModal('none')
  }, [])

  const handleRotateContent = useCallback(
    (dir: 'left' | 'right' | 'flip') => {
      // preview set to CCW
      if (dir === 'flip') {
        setRot((prev) => (prev - 180) % 360)
        currentRot.current += 180
      } else if (dir === 'left') {
        setRot((prev) => (prev - 90) % 360)
        currentRot.current += 90
      } else if (dir === 'right') {
        setRot((prev) => (prev - 270) % 360)
        currentRot.current += 270
      }
    },
    [setRot],
  )

  const handleBlockAccount = useCallback(() => {
    access_token &&
      requestData('forkygram_post_block_account', {
        useDefaultMessage: true,
        actionType: 'execute',
        data: {
          blocked_id: collaboratorOwner.member_id,
        },
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            onBlocked(collaboratorOwner.member_id)
            showSnackbar(translate('forkygram:blockYuserAccSuccess'))
          }
        },
      })
  }, [access_token, collaboratorOwner.member_id, onBlocked, translate])

  const handleReportPost = useCallback(
    (category: ForkygramReportCategoriesResponse, description?: string) => {
      requestData('forkygram_post_report_posts', {
        data: {
          category_id: category.id,
          description,
          reported_id: data.id,
          reported_member_id: collaboratorOwner.member_id,
        },
        onRequestReceived: () => handleToggleModal(),
        onRequestFailed: () =>
          showSnackbar(translate('forkygram:reportPostFailed')),
        onRequestSuccess: ({status}) => {
          onReported(data.id)
          showSnackbar(
            translate(
              status === 200
                ? 'forkygram:reportPostSuccess'
                : 'forkygram:reportPostFailed',
            ),
          )
        },
      })
    },
    [
      collaboratorOwner.member_id,
      data.id,
      handleToggleModal,
      onReported,
      translate,
    ],
  )

  const handleDeletePost = useCallback(() => {
    requestData('forkygram_delete_post', {
      actionType: 'execute',
      useDefaultMessage: true,
      data: {id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          showSnackbar(translate('giftShop:removeContentsSnackbarSuccess'))
          onDeleted(data.id)
        }
      },
    })
  }, [data.id, onDeleted, translate])

  const handleRemoveCollaboration = useCallback(() => {
    if (!collaboratorSelf) return
    requestData('forkygram_delete_collaborator', {
      useDefaultMessage: true,
      data: {post_id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          showSnackbar(translate('tree:removeCollaborationSuccessMessage'))
          onCollabRemoved(collaboratorSelf.member_id)
        }
      },
    })
  }, [collaboratorSelf, data.id, onCollabRemoved, translate])

  const handleClickMore = useCallback(() => {
    if (id === collaboratorOwner.member_id) setSelfModal('more')
    else setModal('options')
  }, [collaboratorOwner.member_id, id])

  const handleFeatureDisabled = useCallback(() => {
    handleToggleModal()
    if (childStatus) {
      showModalLimitedAccess()
      return
    }
    if (kycStatus === 'onhold') {
      if (similarStatus === 'CHOOSE_SELF') {
        return showModalReprocessKyc()
      }
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [childStatus, handleToggleModal, history, kycStatus, similarStatus])

  const handleRenderOptionsModal = useMemo(
    () => (
      <GiftShopTemplateOptionsModal
        onShare={() =>
          childStatus ||
          !(kycStatus === 'verified' || kycStatus === 'verified_check')
            ? handleFeatureDisabled()
            : setModal('share')
        }
        onBlock={() =>
          !(kycStatus === 'verified' || kycStatus === 'verified_check')
            ? handleFeatureDisabled()
            : setModal('block')
        }
        onDelete={
          data.can_delete
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('delete')
            : undefined
        }
        onEdit={
          data.can_edit
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : onEdited()
            : undefined
        }
        onReport={
          !data.can_edit
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('report')
            : undefined
        }
        onRemoveCollab={
          collaboratorSelf
            ? () => {
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('remove-collaboration')
              }
            : undefined
        }
        onRotate={() => setModal('rotate')}
        toggleModal={handleToggleModal}
      />
    ),
    [
      childStatus,
      collaboratorSelf,
      data.can_delete,
      data.can_edit,
      handleFeatureDisabled,
      handleToggleModal,
      kycStatus,
      onEdited,
    ],
  )

  const handleRenderShareModal = useMemo(
    () => (
      <GiftShopTemplateShareModal
        type="post"
        targetId={data.id}
        subTargetId={collaboratorOwner.username}
        caption={data.caption}
        toggleModal={handleToggleModal}
      />
    ),
    [collaboratorOwner.username, data.caption, data.id, handleToggleModal],
  )

  const handleRenderReportModal = useMemo(
    () => (
      <GiftShopTemplateReportModal
        type="post"
        translationKey="giftShop:reportReason"
        onSubmit={handleReportPost}
        toggleModal={() => {
          handleToggleModal()
          showSnackbar(translate('forkygram:reportCancel'))
        }}
      />
    ),
    [handleReportPost, handleToggleModal, translate],
  )

  const handleRenderBlockYuserModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        text={translate('forkygram:blockYuserAccText')}
        title={translate('forkygram:blockYuserAcc')}
        cancelText={translate('global:cancel')}
        onConfirm={handleBlockAccount}
        toggleModal={handleToggleModal}
      />
    ),
    [handleBlockAccount, handleToggleModal, translate],
  )

  const handleRenderValidateKYCModal = useMemo(
    () => <ValidateKYCModal visible toggleModal={handleToggleModal} />,
    [handleToggleModal],
  )

  const handleRenderDeleteModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('forkygram:deletePostTitle')}
        text={translate('forkygram:deletePostMessage')}
        onConfirm={handleDeletePost}
        toggleModal={handleToggleModal}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:delete')}
      />
    ),
    [handleDeletePost, handleToggleModal, translate],
  )

  const handleRenderColaborationModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('tree:removeCollaboration')}
        text={translate('tree:removeCollaborationDescription')}
        onConfirm={handleRemoveCollaboration}
        toggleModal={handleToggleModal}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:remove')}
      />
    ),
    [handleRemoveCollaboration, handleToggleModal, translate],
  )

  const handleFinalizeRotateModal = useCallback(() => {
    handleToggleModal()
    if (currentRot.current % 360 !== 0) {
      requestData('forkygram_rotate_post', {
        data: {post_id: data.id, rotation: currentRot.current % 360},
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            showSnackbar(translate('global:success'))
          } else {
            setRot(rot + (currentRot.current % 360))
            showSnackbar(
              translate('global:messageError', {context: `code-${status}`}),
            )
          }
          currentRot.current = 0
        },
      })
    }
  }, [data.id, handleToggleModal, rot, setRot, translate])

  const handleRenderRotateModal = useMemo(
    () => (
      <GiftShopTemplateOptionsContent
        visible
        toggleModal={handleFinalizeRotateModal}>
        <GiftShopTemplateOptionsItem
          icon="rotate-left"
          text={translate('global:rotateLeft')}
          onClickItem={() => {
            handleRotateContent('left')
          }}
        />
        <GiftShopTemplateOptionsItem
          icon="rotate-right"
          text={translate('global:rotateRight')}
          onClickItem={() => {
            handleRotateContent('right')
          }}
        />
        <GiftShopTemplateOptionsItem
          icon="flip-camera"
          text={translate('global:flipVertical')}
          onClickItem={() => {
            handleRotateContent('flip')
          }}
        />
      </GiftShopTemplateOptionsContent>
    ),
    [handleFinalizeRotateModal, handleRotateContent, translate],
  )

  const handleRenderMoreModal = useMemo(
    () => (
      <TemplateProfileSelfOptionModal
        visible
        toggleModal={handleToggleModal}
        onClickDelete={
          data.can_delete ? () => setSelfModal('delete') : undefined
        }
        onClickShare={() => {
          if (childStatus) {
            handleToggleModal()
            showModalLimitedAccess()
            return
          }
          setSelfModal('share')
        }}
        onClickEdit={data.can_edit ? onEdited : undefined}
      />
    ),
    [childStatus, data.can_delete, data.can_edit, handleToggleModal, onEdited],
  )

  const handleRenderModal = useMemo(() => {
    switch (modal) {
      case 'block':
        return handleRenderBlockYuserModal
      case 'options':
        return handleRenderOptionsModal
      case 'remove-collaboration':
        return handleRenderColaborationModal
      case 'report':
        return handleRenderReportModal
      case 'kyc':
        return handleRenderValidateKYCModal
      case 'delete':
        return handleRenderDeleteModal
      case 'share':
        return handleRenderShareModal
      case 'rotate':
        return handleRenderRotateModal
      case 'none':
        return null
    }
  }, [
    handleRenderBlockYuserModal,
    handleRenderColaborationModal,
    handleRenderDeleteModal,
    handleRenderOptionsModal,
    handleRenderReportModal,
    handleRenderRotateModal,
    handleRenderShareModal,
    handleRenderValidateKYCModal,
    modal,
  ])

  const handleRenderSelfModal = useMemo(() => {
    switch (selfModal) {
      case 'more':
        return handleRenderMoreModal
      case 'delete':
        return handleRenderDeleteModal
      case 'share':
        return handleRenderShareModal
      case 'rotate':
        return handleRenderRotateModal
      case 'remove-collaboration':
      case 'none':
        return null
    }
  }, [
    handleRenderDeleteModal,
    handleRenderMoreModal,
    handleRenderRotateModal,
    handleRenderShareModal,
    selfModal,
  ])

  const handleRenderItem = useMemo(
    () => (
      <ForkygramTemplatePostContent
        isFeed
        hideBottomNavbar={false}
        active={visibilityIndex === index && !pause}
        focused={visibilityIndex === index}
        data={data}
        stateVideoPause={stateVideoPause}
        stateMute={stateMute}
        setVideoPlaying={setVideoPlaying}
        onClickMore={handleClickMore}
        onFollowed={onFollowed}
        stateRot={stateRot}
      />
    ),
    [
      data,
      handleClickMore,
      index,
      onFollowed,
      pause,
      setVideoPlaying,
      stateMute,
      stateRot,
      stateVideoPause,
      visibilityIndex,
    ],
  )

  const handleRenderInput = useMemo(
    () => (
      <InputPicker
        multiple
        inputRef={ref}
        onLoadFiles={(files) =>
          files.length > 0 && history.push('giftshop_send_to_face', {files})
        }
      />
    ),
    [history],
  )

  useDidMount(() => {
    const {content_type} = data
    if (setVideoPlaying && index === 0) {
      setVideoPlaying(content_type === 'video')
    }
  })

  return (
    <>
      {handleRenderModal}
      {handleRenderSelfModal}
      {handleRenderItem}
      {handleRenderInput}
    </>
  )
}
